import { useTranslation } from "react-i18next";

import { SearchableMultiInput } from "@components/SearchableMultiInput";

import { useCreateSpecialty } from "@hooks/mutations/profile/useCreateSpecialty";
import { useGetSpecialties } from "@hooks/queries/profile/useGetSpecialties";


interface SpecialtyOption {
  label: string;
  value: string;
}

interface SpecialtyMultiInputProps {
  label: string;
  placeholder?: string;
  value: SpecialtyOption[];
  onChange: (value: SpecialtyOption[]) => void;
  error?: string;
  withAsterisk?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const SpecialtyMultiInput = ({
  label,
  placeholder,
  value,
  onChange,
  error,
  withAsterisk = false,
  disabled = false,
  required = false,
}: SpecialtyMultiInputProps) => {
  const { t } = useTranslation();

  // Fetch existing specialties
  const { data: specialtiesData = [], isLoading: isLoadingSpecialties } = useGetSpecialties();

  // Create specialty mutation
  const { mutate: createSpecialty, isLoading: isCreatingSpecialty } = useCreateSpecialty();

  const handleCreateSpecialty = (searchTerm: string): Promise<SpecialtyOption> => {
    return new Promise((resolve, reject) => {
      createSpecialty(
        {
          name: searchTerm,
          description: searchTerm, // Use name as description for now
        },
        {
          onSuccess: (response) => {
            // Handle different response structures
            const specialtyData = response.data?.data || response.data;
            const newSpecialty: SpecialtyOption = {
              label: specialtyData.name,
              value: specialtyData._id,
            };
            resolve(newSpecialty);
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  };



  return (
    <SearchableMultiInput
      label={label}
      placeholder={placeholder || t("type-a-specialty")}
      value={value}
      onChange={onChange}
      error={error}
      withAsterisk={withAsterisk}
      disabled={disabled}
      required={required}
      data={specialtiesData}
      isLoading={isLoadingSpecialties}
      onCreateNew={handleCreateSpecialty}
      isCreating={isCreatingSpecialty}
      createButtonText={t("create-specialty")}
      emptyStateText={t("no-specialties-found")}
      loadingText={t("loading")}
    />
  );
};
